import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBlogs,
  getBlogBySlug,
  getBlogCategories,
  getAdminBlogs,
} from "../services/blogService";

const normalizeCategoryOption = (item) => ({
  value: item?.id ?? item?.slug ?? item?.name ?? "",
  label: item?.name ?? item?.label ?? "",
  slug: item?.slug ?? "",
  raw: item,
});

const normalizeAdminBlog = (item) => ({
  ...item,
  content: item?.content_html || item?.content || "",
  cover_image: item?.featured_image || item?.cover_image || item?.coverImage || "",
  hero_bg: item?.hero_bg || item?.heroBg || "",
  meta_title: item?.meta_title || item?.seo_title || "",
  meta_desc: item?.meta_desc || item?.seo_description || "",
});

const useBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [adminBlogs, setAdminBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    featured: false,
  });
  const [loading, setLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchPublicBlogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // PERBAIKAN: Tambahkan parameter limit agar Django tidak memotong datanya
      const params = { limit: 100 }; 
      
      const [blogsRes, categoriesRes] = await Promise.all([
        getBlogs(params), // Mengirim parameter ke service
        getBlogCategories(),
      ]);

      setBlogs(Array.isArray(blogsRes) ? blogsRes : blogsRes?.results || []);
      setCategories(
        (Array.isArray(categoriesRes) ? categoriesRes : categoriesRes?.results || []).map(
          normalizeCategoryOption
        )
      );
    } catch (err) {
      setError(err?.message || "Gagal mengambil data blog.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAdminBlogs = useCallback(async () => {
    setAdminLoading(true);
    setError("");
    try {
      const res = await getAdminBlogs();
      const normalized = (Array.isArray(res) ? res : res?.results || []).map(
        normalizeAdminBlog
      );
      setAdminBlogs(normalized);
    } catch (err) {
      setError(err?.message || "Gagal mengambil data blog admin.");
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const fetchBlogDetail = useCallback(async (slug) => {
    setLoading(true);
    setError("");
    try {
      const res = await getBlogBySlug(slug);
      setSelectedBlog(res);
      return res;
    } catch (err) {
      setError(err?.message || "Gagal mengambil detail blog.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPublicBlogs();
  }, [fetchPublicBlogs]);

  const filteredBlogs = useMemo(() => {
    return blogs.filter((item) => {
      const matchSearch = filters.search
        ? item?.title?.toLowerCase().includes(filters.search.toLowerCase())
        : true;

      const matchCategory = filters.category
        ? item?.category_name === filters.category ||
          item?.category === filters.category ||
          item?.category_slug === filters.category
        : true;

      const matchFeatured = filters.featured ? Boolean(item?.is_featured) : true;

      return matchSearch && matchCategory && matchFeatured;
    });
  }, [blogs, filters]);

  return {
    blogs,
    adminBlogs,
    categories,
    selectedBlog,
    filteredBlogs,
    filters,
    setFilters,
    loading,
    adminLoading,
    error,
    setError,
    fetchPublicBlogs,
    fetchAdminBlogs,
    fetchBlogDetail,
    setSelectedBlog,
  };
};

export default useBlogs;